import React, { useState, useEffect, useContext } from 'react';
import IsScrolling from 'react-is-scrolling';
import ReactTooltip from 'react-tooltip';
import StarRating from '../../ui_kit/utilities/star_rating/index.jsx';
import Context from '../../../utils/context.js';
import CompleteDateRangePicker from '../../ui_kit/inputs/complete_date_range_picker/index.jsx';
import BarChart from '../../ui_kit/charts/bar_chart/index.jsx';
import StackedBarChart from '../../ui_kit/charts/stacked_bar_chart/index.jsx';
import ExportButton from '../../ui_kit/buttons/export_button/index.jsx';

import { getPatientInsights } from '../../../api/patient_insights.js';
import { getPatientInsightsExport } from '../../../api/patient_insights.js';

import styles from './index.module.css';

function PatientInsights({ isScrolling }) {
  const [totalReadyForNextStep, setTotalReadyForNextStep] = useState();
  const [averageConversationRating, setAverageConversationRating] = useState();

  // Conversation topics
  const [conversationTopics, setConversationTopics] = useState();

  // Patient funnel
  const [patientFunnel, setPatientFunnel] = useState();

  // Impact of conversation
  const [conversationImpact, setConversationImpact] = useState();

  // Conversation completed by patient type
  const [conversationCompletedByPatientType, setConversationCompletedByPatientType] = useState();

  // Ready for next step by patient type
  const [nextStepByPatientType, setNextStepByPatientType] = useState();

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const context = useContext(Context);
  const currentUser = context.auth.getCurrentUser();
  const currentAccount = context.auth.currentAccount();
  const isNotBear = currentAccount && currentAccount.slug != 'bear';
  const toolTipText = currentUser.is_xcopri
    ? 'Patients who are planning or may be planning to start or continue treatment with XCOPRI.'
    : 'This is the number of people who answered they were ready for the next step on a survey.';
  const readyForNextStepText = currentUser.is_xcopri
    ? 'will start or continue treatment'
    : 'patient(s) ready for the next step';

  useEffect(() => {
    let cancelled = false;

    if (isInitialLoad) {
      setIsInitialLoad(false);
    } else {
      if (!startDate || !endDate) {
        return;
      }
    }

    let params = {};

    if (startDate !== null && endDate !== null) {
      params = {
        ...params,
        ...(startDate !== null && { start_date: startDate.toISOString().split('T')[0] }),
        ...(endDate !== null && { end_date: endDate.toISOString().split('T')[0] }),
      };
    }

    loadData(params, () => cancelled);

    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  const loadData = async (params, isCancelled = () => false) => {
    try {
      new Promise((resolve, reject) => {
        getPatientInsights(params, (data) => {
          if (isCancelled()) {
            resolve();
            return;
          }

          try {
            const stateUpdates = {
              total_ready_for_next_step: setTotalReadyForNextStep,
              average_conversation_rating: setAverageConversationRating,
              patient_funnel: setPatientFunnel,
              conversation_impact: setConversationImpact,
              conversation_completed_by_patient_type: setConversationCompletedByPatientType,
              next_step_by_patient_type: setNextStepByPatientType,
              conversation_topics: setConversationTopics,
            };

            Object.entries(data).forEach(([key, value]) => {
              if (stateUpdates[key]) {
                stateUpdates[key](value);
              }
            });
            resolve();
          } catch (error) {
            reject(error);
          }
        });
      });
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  return (
    <div className='container marg-bottom-50'>
      <div className='row'>
        <h1 className={`${styles.header} col-lg-6`}>Insights</h1>
        <CompleteDateRangePicker
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          startDate={startDate}
          endDate={endDate}
          isScrolling={isScrolling}
          style={{ marginLeft: 'auto' }}
        />
      </div>
      <div className='row' style={{ marginBottom: '0px' }}>
        <h1 className={`${styles.small_header} col-lg-6`}>Patient connection conversions</h1>
        <ExportButton
          requestExport={getPatientInsightsExport}
          params={{
            ...(startDate && { start_date: startDate.toISOString().split('T')[0] }),
            ...(endDate && { end_date: endDate.toISOString().split('T')[0] }),
          }}
        />
      </div>
      <div className='row'>
        <div className='col-md-6'>
          <div className='content_container'>
            <ReactTooltip id='readyForNextStep' place='top'>
              <p style={{ color: '#fff', fontSize: '14px', fontWeight: '600', lineHeight: '16px' }}>{toolTipText}</p>
            </ReactTooltip>
            {totalReadyForNextStep && (
              <p className={styles.blue_text}>
                {totalReadyForNextStep?.results?.true ?? 0} / {totalReadyForNextStep.total}
              </p>
            )}
            {totalReadyForNextStep === undefined && <div className={styles.blue_text}>no data</div>}
            <p data-tip data-for='readyForNextStep' className={styles.black_text}>
              {readyForNextStepText}
            </p>
          </div>
        </div>
        <div className='col-md-6'>
          <div className='content_container'>
            <ReactTooltip id='conversationRating' place='top'>
              <p style={{ color: '#fff', fontSize: '14px', fontWeight: '600', lineHeight: '16px' }}>
                This is the average conversation rating provided by users on a survey.
              </p>
            </ReactTooltip>
            {averageConversationRating && (
              <div style={{ display: 'flex' }} className='justify-content-center'>
                <StarRating averageRating={averageConversationRating.results} />
              </div>
            )}
            {averageConversationRating === undefined && <div className={styles.blue_text}>no data</div>}
            <p data-tip data-for='conversationRating' className={`row justify-content-center ${styles.black_text}`}>
              avg. conversation satisfaction rating
            </p>
          </div>
        </div>
      </div>
      <div className='row'>
        <div className='col-lg-12'>
          <div className='content_container'>
            <BarChart title='Patients Conversions' xTitle='Number of patients' yTitle='' data={patientFunnel} />
          </div>
        </div>
      </div>
      {isNotBear && (
        <div className='row'>
          <div className='col-lg-12'>
            <div className='content_container'>
              <StackedBarChart
                title='Conversation Impact'
                xTitle='Number of mentees'
                yTitle=''
                data={conversationImpact}
              />
            </div>
          </div>
        </div>
      )}
      <div className='row'>
        <div className='col-lg-12'>
          <div className='content_container'>
            <StackedBarChart
              title='Mentees Who Completed Conversations by Care Stage'
              xTitle='Number of mentees'
              yTitle=''
              data={conversationCompletedByPatientType}
            />
          </div>
        </div>
      </div>
      <div className='row'>
        <div className='col-lg-12'>
          <div className='content_container'>
            <StackedBarChart
              title='Mentees Ready for the Next Step by Care Stage'
              xTitle='Number of mentees'
              yTitle=''
              data={nextStepByPatientType}
            />
          </div>
        </div>
      </div>
      <div className='row'>
        <div className='col-lg-12'>
          <div className='content_container'>
            <BarChart
              title="Topics Your Mentors Help Mentee's With"
              xTitle='Number of mentees'
              yTitle=''
              data={conversationTopics}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default IsScrolling(PatientInsights);
